import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { dataAPI, sitesAPI } from '../utils/api';
import {
  Database, Upload, RefreshCw, CheckCircle, XCircle, Download,
  FileSpreadsheet, Copy, Check, Info, Zap, Battery, Activity,
  Gauge, Radio, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DataIngestionPage = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => { fetchSites(); }, []);

  const fetchSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) { toast.error('Please select a CSV file'); return; }
      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    if (!selectedSite) { toast.error('Please select a site'); return; }
    setUploading(true);
    try {
      const response = await dataAPI.uploadCSV(file, selectedSite);
      setUploadResult(response.data);
      if (response.data.success) {
        toast.success(`Successfully processed ${response.data.records_processed} records`);
      } else {
        toast.warning(`Processed ${response.data.records_processed} records with ${response.data.records_failed} failures`);
      }
      setFile(null);
    } catch (error) {
      toast.error('Failed to upload CSV file');
    } finally { setUploading(false); }
  };

  const handleVendorSync = async () => {
    setSyncing(true);
    try {
      const response = await dataAPI.triggerVendorSync();
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to trigger vendor sync');
    } finally { setSyncing(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          <PageHeader title="Data Ingestion" description="Upload CSV files, sync vendor APIs, and view data specifications" />

          <Tabs defaultValue="upload" className="mt-2">
            <TabsList className="bg-white border border-slate-200 p-1 mb-6">
              <TabsTrigger value="upload" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <Upload className="w-4 h-4 mr-2" /> Upload & Sync
              </TabsTrigger>
              <TabsTrigger value="spec" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-data-spec">
                <ClipboardList className="w-4 h-4 mr-2" /> Vendor Data Spec
              </TabsTrigger>
            </TabsList>

            {/* Upload & Sync Tab */}
            <TabsContent value="upload">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* CSV Upload */}
                <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center">
                      <Upload className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">CSV Upload</h3>
                      <p className="text-sm text-slate-600">Upload telemetry data from CSV files</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="site-select" className="text-slate-700 font-medium mb-2 block">Select Site</Label>
                      <Select value={selectedSite} onValueChange={setSelectedSite}>
                        <SelectTrigger id="site-select" data-testid="site-select"><SelectValue placeholder="Choose a site" /></SelectTrigger>
                        <SelectContent>
                          {sites.map((site) => (<SelectItem key={site.site_id} value={site.site_id}>{site.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="csv-file" className="text-slate-700 font-medium mb-2 block">CSV File</Label>
                      <Input id="csv-file" data-testid="csv-file-input" type="file" accept=".csv" onChange={handleFileChange} className="cursor-pointer" />
                      {file && <p className="text-sm text-slate-600 mt-2">Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)</p>}
                    </div>
                    <Button onClick={handleUpload} disabled={uploading || !file || !selectedSite} data-testid="upload-csv-button" className="w-full bg-slate-900 hover:bg-slate-800">
                      {uploading ? 'Uploading...' : 'Upload CSV'}
                    </Button>
                    {uploadResult && (
                      <div className={`p-4 rounded-sm border ${uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex items-start space-x-3">
                          {uploadResult.success ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />}
                          <div className="flex-1">
                            <p className={`text-sm font-medium mb-1 ${uploadResult.success ? 'text-green-800' : 'text-amber-800'}`}>
                              Upload {uploadResult.success ? 'Successful' : 'Completed with Errors'}
                            </p>
                            <p className="text-sm text-slate-600">Processed: {uploadResult.records_processed} | Failed: {uploadResult.records_failed}</p>
                            {uploadResult.errors?.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-slate-700 mb-1">Errors:</p>
                                <ul className="text-xs text-slate-600 space-y-1">
                                  {uploadResult.errors.map((error, idx) => <li key={idx}>{error}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vendor API Sync */}
                <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-green-50 rounded-sm flex items-center justify-center">
                      <Database className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Vendor API Sync</h3>
                      <p className="text-sm text-slate-600">Sync data from vendor APIs</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                      <h4 className="text-sm font-medium text-slate-900 mb-3">Configured Vendors</h4>
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-slate-700">Mock Vendor API</span>
                        </div>
                        <span className="text-xs text-slate-500">Active</span>
                      </div>
                    </div>
                    <Button onClick={handleVendorSync} disabled={syncing} data-testid="vendor-sync-button" className="w-full bg-green-600 hover:bg-green-700">
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Trigger Vendor Sync'}
                    </Button>
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="text-sm font-medium text-slate-900 mb-3">Sync Schedule</h4>
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex justify-between"><span>Frequency:</span><span className="font-medium text-slate-900">Every 15 minutes</span></div>
                        <div className="flex justify-between"><span>Last Sync:</span><span className="font-medium text-slate-900">2 minutes ago</span></div>
                        <div className="flex justify-between"><span>Next Sync:</span><span className="font-medium text-slate-900">In 13 minutes</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Data Spec Tab */}
            <TabsContent value="spec">
              <DataSpecSection />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

/* ===== Data Specification Section ===== */

const FIELD_GROUPS = [
  {
    title: 'Identification', icon: FileSpreadsheet, color: 'blue',
    description: 'Fields to identify which site/asset the data belongs to',
    fields: [
      { name: 'timestamp', type: 'ISO 8601 String', required: true, aliases: ['Timestamp', 'Date Time', 'DateTime'], example: '2026-03-04T08:00:00Z', description: 'Reading date and time in UTC or with timezone offset' },
      { name: 'site_name', type: 'String', required: true, aliases: ['Site Name', 'site_id', 'Site ID'], description: 'Site name (matched in system) or site UUID. One of site_name or site_id is required.' },
      { name: 'bess_asset_id', type: 'String', required: false, aliases: ['asset_id', 'BMS Asset ID', 'Asset ID'], description: 'BESS asset identifier. If omitted, data is assigned to the primary asset at the site.' },
    ],
  },
  {
    title: 'State of Charge & Health', icon: Battery, color: 'green',
    description: 'Battery state metrics — SoC is critical for monitoring',
    fields: [
      { name: 'soc_percent', type: 'Float (0-100)', required: true, aliases: ['soc', 'SOC', 'SoC', 'State of Charge'], example: '85.5', description: 'State of Charge as percentage' },
      { name: 'soh_percent', type: 'Float (0-100)', required: false, aliases: ['soh', 'SOH', 'State of Health'], example: '97.2', description: 'State of Health as percentage' },
      { name: 'rcc_percent', type: 'Float (0-100)', required: false, aliases: ['rcc', 'RCC'], example: '92.3', description: 'Remaining Cell Capacity percentage' },
      { name: 'temperature_c', type: 'Float', required: false, aliases: ['temperature', 'Temperature'], example: '28.5', description: 'Battery temperature in Celsius' },
    ],
  },
  {
    title: 'Power Metrics', icon: Zap, color: 'amber',
    description: 'Instantaneous and energy-based charge/discharge readings',
    fields: [
      { name: 'charge_power_kw', type: 'Float', required: true, aliases: ['Charge Power', 'charge_power'], example: '150.5', description: 'Instantaneous charging power in kW' },
      { name: 'discharge_power_kw', type: 'Float', required: true, aliases: ['Discharge Power', 'discharge_power'], example: '180.5', description: 'Instantaneous discharging power in kW' },
      { name: 'charge_power_kwh', type: 'Float', required: false, aliases: ['Charge Energy', 'charge_energy_kwh'], example: '45.2', description: 'Energy charged in this interval (kWh)' },
      { name: 'discharge_power_kwh', type: 'Float', required: false, aliases: ['Discharge Energy', 'discharge_energy_kwh'], example: '45.8', description: 'Energy discharged in this interval (kWh)' },
      { name: 'voltage_v', type: 'Float', required: false, aliases: ['voltage', 'Voltage'], example: '400.5', description: 'Battery voltage in Volts' },
      { name: 'current_a', type: 'Float', required: false, aliases: ['current', 'Current'], example: '125.3', description: 'Battery current in Amps' },
      { name: 'power_kw', type: 'Float', required: false, aliases: ['power', 'Power'], example: '150.0', description: 'Net power output in kW' },
    ],
  },
  {
    title: 'Cumulative Metering', icon: Gauge, color: 'purple',
    description: 'Running totals — essential for billing calculations',
    fields: [
      { name: 'cumulative_discharge_kwh', type: 'Float', required: true, aliases: ['cumulative_discharge', 'Cumulative Discharge'], example: '6200.8', description: 'Total cumulative energy discharged since meter start (kWh). Primary billing metric.' },
      { name: 'cumulative_charge_kwh', type: 'Float', required: true, aliases: ['cumulative_charge', 'Cumulative Charge'], example: '5500.5', description: 'Total cumulative energy charged since meter start (kWh)' },
      { name: 'interval_discharge_kwh', type: 'Float', required: false, aliases: ['Interval Discharge', 'interval_kwh'], example: '12.5', description: 'Discharge energy for this specific interval. Fallback if cumulative unavailable.' },
      { name: 'internal_charge_kwh', type: 'Float', required: false, aliases: ['Internal Charge', 'internal_charge'], example: '10.5', description: 'Internal charge energy (kWh)' },
      { name: 'internal_discharge_kwh', type: 'Float', required: false, aliases: ['Internal Discharge', 'internal_discharge'], example: '8.2', description: 'Internal discharge energy (kWh)' },
    ],
  },
  {
    title: 'Status Fields', icon: Radio, color: 'slate',
    description: 'Operational status indicators from the BMS/EMS',
    fields: [
      { name: 'site_status', type: 'String', required: false, aliases: ['Site Status'], example: 'online', description: 'Overall site status (online, offline, maintenance)' },
      { name: 'inverter_status', type: 'String', required: false, aliases: ['Inverter Status'], example: 'running', description: 'Inverter operational status' },
      { name: 'operating_mode', type: 'String', required: false, aliases: ['Operating Mode'], example: 'charging', description: 'Current mode (charging, discharging, idle, standby)' },
      { name: 'communication_status', type: 'String', required: false, aliases: ['Communication Status'], example: 'connected', description: 'BMS communication link status' },
      { name: 'soc_status', type: 'String', required: false, aliases: ['SOC Status'], example: 'normal', description: 'SoC status indicator' },
      { name: 'rm_status', type: 'String', required: false, aliases: ['RM Status'], example: 'active', description: 'Remote monitoring status' },
      { name: 'quality_flag', type: 'String', required: false, aliases: ['Quality Flag'], example: 'good', description: 'Data quality (good, estimated, missing). Defaults to "good".' },
    ],
  },
];

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={handle} className="p-1 hover:bg-slate-100 rounded transition-colors" title="Copy">
      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
    </button>
  );
};

const DataSpecSection = () => {
  const handleDownload = (type) => {
    const token = localStorage.getItem('token');
    const url = type === 'full' ? `${BACKEND_URL}/api/templates/csv-full` : `${BACKEND_URL}/api/templates/csv-minimal`;
    window.open(`${url}?token=${token}`, '_blank');
    toast.success(`${type === 'full' ? 'Full' : 'Minimal'} template download started`);
  };

  const handleCopyRequired = () => {
    const all = FIELD_GROUPS.flatMap(g => g.fields.filter(f => f.required).map(f => f.name));
    navigator.clipboard.writeText(all.join(','));
    toast.success('Required field names copied to clipboard');
  };

  const requiredCount = FIELD_GROUPS.reduce((s, g) => s + g.fields.filter(f => f.required).length, 0);
  const totalCount = FIELD_GROUPS.reduce((s, g) => s + g.fields.length, 0);
  const colors = { blue: 'border-l-blue-500', green: 'border-l-green-500', amber: 'border-l-amber-500', purple: 'border-l-purple-500', slate: 'border-l-slate-400' };

  return (
    <div className="max-w-5xl">
      {/* Summary */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="spec-summary">
        <div className="flex items-center space-x-6">
          <div><p className="text-xs text-slate-500 uppercase tracking-wide">Required</p><p className="text-2xl font-bold text-slate-900">{requiredCount}</p></div>
          <div className="w-px h-10 bg-slate-200" />
          <div><p className="text-xs text-slate-500 uppercase tracking-wide">Total Fields</p><p className="text-2xl font-bold text-slate-500">{totalCount}</p></div>
          <div className="w-px h-10 bg-slate-200" />
          <div><p className="text-xs text-slate-500 uppercase tracking-wide">Format</p><p className="text-lg font-bold text-slate-900">CSV (UTF-8)</p></div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleCopyRequired} data-testid="copy-required-fields-btn"><Copy className="w-4 h-4 mr-2" /> Copy Required</Button>
          <Button variant="outline" size="sm" onClick={() => handleDownload('minimal')} data-testid="download-minimal-btn"><Download className="w-4 h-4 mr-2" /> Minimal CSV</Button>
          <Button size="sm" className="bg-slate-900 text-white" onClick={() => handleDownload('full')} data-testid="download-full-btn"><Download className="w-4 h-4 mr-2" /> Full Template</Button>
        </div>
      </div>

      {/* Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Column Name Flexibility</p>
          <p className="text-blue-800">Each field accepts multiple column name formats (shown as "Aliases" below). Both <code className="bg-blue-100 px-1 rounded text-xs">snake_case</code> and <code className="bg-blue-100 px-1 rounded text-xs">Title Case</code> are supported.</p>
        </div>
      </div>

      {/* Grouped view */}
      <div className="space-y-6">
        {FIELD_GROUPS.map((group) => {
          const Icon = group.icon;
          return (
            <div key={group.title} className={`bg-white border border-slate-200 border-l-4 ${colors[group.color]} rounded-sm shadow-sm`} data-testid={`field-group-${group.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5 text-slate-600" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{group.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{group.description}</p>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-slate-50">
                {group.fields.map((field) => (
                  <div key={field.name} className="px-5 py-3 flex items-start gap-4 hover:bg-slate-50/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <code className="text-sm font-mono font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{field.name}</code>
                        <CopyBtn text={field.name} />
                        {field.required
                          ? <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Required</span>
                          : <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200">Optional</span>}
                        <span className="text-xs text-slate-400">{field.type}</span>
                      </div>
                      <p className="text-sm text-slate-600">{field.description}</p>
                      {field.aliases?.length > 0 && (
                        <div className="mt-1.5 flex items-center flex-wrap gap-1">
                          <span className="text-[10px] text-slate-400 mr-1">Aliases:</span>
                          {field.aliases.map((a) => <code key={a} className="text-[11px] bg-slate-50 text-slate-600 px-1.5 py-0.5 rounded border border-slate-100">{a}</code>)}
                        </div>
                      )}
                    </div>
                    {field.example && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-[10px] text-slate-400 uppercase">Example</p>
                        <code className="text-xs text-slate-700 font-mono">{field.example}</code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
